import {observable, action} from 'mobx';
import {message} from '@enos/dpl';
import localeText from '@locale';
import Pagination from './Pagination';
import axiosInstance from '~/utils/axiosInstance';
import uris from '~/uris';

export class SubAdminStore extends Pagination {
  @observable subAdmins = [];

  @observable currentSubAdmin = {};

  @observable isLoading = false;

  @observable isShowLoading = false;

  @observable userList = [];

  @observable structureList = [];

  @observable userGroupList = [];

  @observable roleList = [];

  @observable selectedUserIds = [];

  @observable selectedStructureList = [];

  @observable selectedUserGroupIds = [];

  @observable selectedRoleIds = [];

  @observable operationList = [];

  @observable selectedOperationIds = [];

  @observable subAdminsLength;

  @action.bound
  setLoading(boolean) {
    this.isLoading = boolean;
  }

  @action.bound
  setShowLoading(boolean) {
    this.isShowLoading = boolean;
  }

  @action.bound
  setSubAdmins(data) {
    this.subAdmins = data;
  }

  @action.bound
  setSubAdminsLength(num) {
    this.subAdminsLength = num;
  }

  @action.bound
  setCurrentSubAdmin(data) {
    this.currentSubAdmin = data;
  }

  @action.bound
  setUserList(data) {
    this.userList = data;
  }

  @action.bound
  setSelectedUserIds(value) {
    this.selectedUserIds = value;
  }

  @action.bound
  setStructureList(data) {
    this.structureList = data;
  }

  @action.bound
  setUserGroupList(data) {
    this.userGroupList = data;
  }

  @action.bound
  setRoleList(data) {
    this.roleList = data;
  }

  @action.bound
  setSelectedStructureList(data) {
    this.selectedStructureList = data;
  }

  @action.bound
  setSelectedUserGroupIds(data) {
    this.selectedUserGroupIds = data;
  }

  @action.bound
  setSelectedRoleIds(data) {
    this.selectedRoleIds = data;
  }

  @action.bound
  setOperationList(data) {
    this.operationList = data;
  }

  @action.bound
  setSelectedOperationIds(value) {
    this.selectedOperationIds = value;
  }

  @action.bound
  async getSubAdminList(body = {}) {
    this.setLoading(true);
    await axiosInstance
      .post(uris.getSubAdminList(), {
        ...body
      })
      .then((data) => {
        this.setSubAdmins(data.users);
        this.setPagination(data.pagination);
        this.setSubAdminsLength((data.users || []).length);
        this.setLoading(false);
      })
      .catch(() => this.setLoading(false));
  }

  @action.bound
  getSubAdminListWithAddition(body = {}) {
    let data = {
      ...this.paginationRequestBody
    };

    if (this.subAdminsLength === 1 && body.isDelete) {
      data = {
        ...this.paginationRequestBodyBefore
      };
    }

    if (body.forceFresh) {
      data = {
        ...this.initPaginationRequestBody
      };
    }

    return this.getSubAdminList({
      ...data,
      ...body
    });
  }

  @action.bound
  changePagination(pagination) {
    super.changePagination(pagination);
    this.getSubAdminListWithAddition();
  }

  @action.bound
  removeSubAdmin(id) {
    this.isLoading = true;
    axiosInstance
      .post(uris.removeSubAdmin(), null, {
        params: {userId: id},
        showError: {
          showPopup: true,
          title: localeText('SUB_ADMIN.TITLE_REMOVE_FAIL')
        }
      })
      .then(() => {
        message.success(localeText('SUB_ADMIN.MSG_REMOVE_SUCCESS'));
        this.setLoading(false);
        this.getSubAdminListWithAddition({isDelete: true});
      })
      .catch(() => {
        this.setLoading(false);
      });
  }

  @action.bound
  getSubAdmin(id) {
    this.setLoading(true);
    axiosInstance
      .get(uris.getSubAdmin(), {
        params: {
          userId: id
        }
      })
      .then((data) => {
        this.setLoading(false);
        this.setCurrentSubAdmin(data);
        this.setSelectedOperationIds(data?.operations.map(operation => operation.id));
        this.setSelectedStructureList(data?.structures);
        this.setSelectedUserGroupIds((data?.userGroups || []).map(userGroup => userGroup.id));
        this.setSelectedRoleIds((data?.roles || []).map(role => role.id));
      })
      .catch(() => {
        this.setLoading(false);
      });
  }

  @action.bound
  getUserListWithFilter(body = {}, query = {}) {
    this.setShowLoading(true);
    axiosInstance
      .post(
        uris.getSubAdminCreateUserList(),
        {
          ...body
        },
        {
          params: {
            ...query
          }
        }
      )
      .then((data) => {
        this.setShowLoading(false);
        this.setUserList(data.users);
      })
      .catch(() => this.setShowLoading(false));
  }

  @action.bound
  getSubAdminStructureList(body = {}, query = {}) {
    axiosInstance
      .post(
        uris.getSubAdminStructureList(),
        {
          ...body
        },
        {
          params: {
            ...query
          }
        }
      )
      .then((data) => {
        this.setStructureList(data.structures);
        if (data.structures && data.structures.length) {
          (data.structures).map(item => this.getSubAdminStructureChildren(item.id, query.userId)
          );
        }
        this.setLoading(false);
      })
      .catch(() => this.setLoading(false));
  }

  @action.bound
  getSubAdminStructureChildren(structureId, userId) {
    return axiosInstance
      .post(uris.getSubAdminStructureChildren(), null, {
        params: {
          structureId,
          userId
        }
      })
      .then((data) => {
        this.setStructureChildren(structureId, data.structures, userId);
      });
  }

  @action.bound
  setStructureChildren(parentId, data, userId) {
    const parent = this.findStructureInStructureTree(parentId, this.structureList, null);
    if (parent) {
      (data || []).forEach((l) => {
        l.parentIds = (parent.parentIds || []).concat(parentId);
        if (parent.children && !parent.children.some(i => i.id === l.id)) {
          parent.children.push(l);
        } else {
          parent.children = [];
          parent.children.push(l);
        }
        if (!l.isLeaf) {
          this.getSubAdminStructureChildren(l.id, userId);
        }
      });
    }
  }

  @action.bound
  findStructureInStructureTree(id, data, preStructure) {
    let structure = preStructure || null;
    data.some((item) => {
      if (item.id === id) {
        structure = item;
        return true;
      }
      if (item.children && item.children.length > 0) {
        structure = this.findStructureInStructureTree(id, item.children, structure);
        return false;
      }
      return false;
    });
    return structure;
  }

  @action.bound
  getUserGroupList(body = {}, query = {}) {
    axiosInstance
      .post(
        uris.getUserGroupList(),
        {
          ...body
        },
        {
          params: {
            ...query
          }
        }
      )
      .then((data) => {
        this.setUserGroupList(data.userGroups);
        this.setLoading(false);
      })
      .catch(() => this.setLoading(false));
  }

  @action.bound
  getSubAdminRoleList(body = {}, query = {}) {
    axiosInstance
      .post(
        uris.getSubAdminRoleList(),
        {
          ...body
        },
        {
          params: {
            ...query
          }
        }
      )
      .then((data) => {
        this.setRoleList(data.roles);
        this.setLoading(false);
      })
      .catch(() => this.setLoading(false));
  }

  @action.bound
  getOperationList() {
    this.setShowLoading(true);
    axiosInstance
      .post(uris.getOperationList())
      .then((data) => {
        this.setShowLoading(false);
        this.setOperationList(data.operations);
      })
      .catch(() => this.setShowLoading(false));
  }

  @action.bound
  createOrModifySubAdmin(type, body = {}) {
    this.setLoading(true);
    return axiosInstance
      .post(uris.createOrModifySubAdmin(), {
        ...body
      })
      .then(() => {
        message.success(localeText(`SUB_ADMIN.MSG_${type === 'create' ? 'CREATE' : 'UPDATE'}_SUCCESS`));
        this.setLoading(false);
      })
      .catch(() => this.setLoading(false));
  }

  @action.bound
  clearState() {
    this.setSelectedUserIds([]);
    this.setSelectedStructureList([]);
    this.setSelectedRoleIds([]);
    this.setSelectedUserGroupIds([]);
    this.setSelectedOperationIds([]);
    this.setUserList([]);
    this.setStructureList([]);
    this.setRoleList([]);
    this.setUserGroupList([]);
  }
}

export default new SubAdminStore();
